import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { format, subMonths } from 'date-fns';
import { supabase } from '../lib/supabaseClient';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const options = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
    },
    title: {
      display: true,
      text: 'Price History',
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        label: function (context) {
          return `Price: $${context.parsed.y.toFixed(2)}`;
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: false,
      ticks: {
        callback: (value) => `$${value.toFixed(2)}`
      }
    },
    x: {
      grid: {
        display: false
      }
    }
  },
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false
  }
};

async function fetchPriceHistory(asin) {
  if (!asin) {
    console.warn('Missing ASIN in fetchPriceHistory');
    return [];
  }

  const { data, error } = await supabase
  .from('products')
  .select('price_history')
  .eq('asin', asin)
  .single();

  if (error) {
    console.error('Error fetching price history:', error);
    return [];
  }

  return data?.price_history || [];
}

export function PriceHistoryChart({ asin }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['priceHistory', asin],
    queryFn: () => fetchPriceHistory(asin),
    enabled: !!asin, // only run if asin exists
  });

  if (isLoading) {
    return (
      <div className="h-[400px] flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Loading price history...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-[400px] flex items-center justify-center">
        <div className="text-center text-red-600 py-4">
          <p>Failed to load price history</p>
          <p className="text-sm text-gray-500 mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="h-[400px] flex items-center justify-center">
        <div className="text-center text-gray-600 py-4">
          <p>No price history available</p>
          <p className="text-sm text-gray-500 mt-1">Price tracking will begin once data is collected</p>
        </div>
      </div>
    );
  }

  const chartData = {
    labels: data.map(item => format(new Date(item.timestamp), 'MMM d')),
    datasets: [
      {
        label: 'Price',
        data: data.map(item => parseFloat(item.price)),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.1,
        fill: true,
        pointBackgroundColor: 'rgb(59, 130, 246)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6
      }
    ]
  };

  return (
    <div className="h-[400px]">
      <Line options={options} data={chartData} />
    </div>
  );
}
